import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Checkbox, CircularProgress } from '@mui/material';
import cx from 'classnames';
import { Notification, ReactBB } from '@/common/utils/index';
import styles from '@/common/containers/TaskDetails/styles.less';
import Notes from '@/common/containers/TaskDetails/components/Notes';
import { DetailsContext } from '@/common/containers/TaskDetails/components/DetailsProvider';
import {
  ASSET_VALUES,
  FIN_INSTITUTIONS,
  FINAL_DISPOSITIONS,
  INTENDED_PLANS,
} from '@/common/constants/constants';
import SingleAnswerForm from '@/common/containers/TaskDetails/components/SingleAnswerForm';
import { useLocalizedField } from '@/common/hooks/index';
import { useTranslation } from 'react-i18next';
import { useCurrentDecedentId } from '@/app/providers/DecedentProvider';
import { useDownloadEstateDispositionSummaryMutation } from '@/entities/report';

const ESTATE_DISPOSITION_SUMMARY_URL =
  '/api/question/estate-disposition-summary';
const ESTATE_DISPOSITION_SUMMARY_FILE_NAME =
  'estate-assets-and-liabilities-final-disposition-summary.pdf';

const Instructions = ({ list = [], onUpdate, disabled = false, taskUid }) => {
  const { t } = useTranslation();
  const decedentId = useCurrentDecedentId();
  const finValues = useRef({});
  const [loadingTasks, setLoadingTasks] = useState(false);
  const getLocalizedField = useLocalizedField();
  const {
    isPending: isEstateDispositionSummaryDownloading,
    mutateAsync: downloadEstateDispositionSummaryFile,
  } = useDownloadEstateDispositionSummaryMutation();
  const {
    setIntendedPlan,
    setAssetValues,
    openFinalDisposition,
    setDispositionData,
    setFinValues,
  } = useContext(DetailsContext);

  const onDataEntryClick = useCallback(
    ({ target: { id } }) => {
      if (FINAL_DISPOSITIONS.includes(id)) {
        openFinalDisposition({
          hideSaleProceeds: taskUid === 'CL06',
        });
      }
      if (FIN_INSTITUTIONS.includes(id)) setFinValues(finValues.current[id]);
    },
    [openFinalDisposition, setFinValues, taskUid]
  );

  useEffect(() => {
    document.addEventListener('onLinkClick', onDataEntryClick);
    return () => {
      document.removeEventListener('onLinkClick', onDataEntryClick);
    };
  }, [onDataEntryClick]);

  const downloadEstateDispositionSummary = useCallback(async () => {
    if (isEstateDispositionSummaryDownloading) return;

    try {
      const blob = await downloadEstateDispositionSummaryFile(decedentId);
      const newBlob = new Blob([blob], {
        type: blob.type || 'application/pdf',
      });
      const blobUrl = window.URL.createObjectURL(newBlob);

      let link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', ESTATE_DISPOSITION_SUMMARY_FILE_NAME);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      link = undefined;
    } catch (_error) {
      Notification.error(t('Downloading report error'));
    }
  }, [
    decedentId,
    downloadEstateDispositionSummaryFile,
    isEstateDispositionSummaryDownloading,
    t,
  ]);

  const onInstructionsClick = useCallback(
    (event) => {
      const link = event.target.closest?.('a');
      const href = link?.getAttribute('href');

      if (
        href !== ESTATE_DISPOSITION_SUMMARY_URL &&
        !href?.endsWith(ESTATE_DISPOSITION_SUMMARY_URL)
      ) {
        return;
      }

      event.preventDefault();
      downloadEstateDispositionSummary();
    },
    [downloadEstateDispositionSummary]
  );

  const updateTask = async (checked, id, isLast) => {
    try {
      setLoadingTasks(true);
      await onUpdate(id, checked, isLast);
      setLoadingTasks(false);
    } catch (e) {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    list.forEach(
      ({ additionalAnswerType, additionalAnswerOptions, answers, id }) => {
        if (INTENDED_PLANS.includes(additionalAnswerType)) {
          setIntendedPlan({
            taskStepId: id,
            options: additionalAnswerOptions,
            answers,
          });
        }
        if (ASSET_VALUES.includes(additionalAnswerType)) {
          setAssetValues({
            taskStepId: id,
            options: additionalAnswerOptions,
            answers,
          });
        }
        if (FINAL_DISPOSITIONS.includes(additionalAnswerType)) {
          setDispositionData({
            taskStepId: id,
            question: additionalAnswerOptions,
            answers,
          });
        }
        if (FIN_INSTITUTIONS.includes(additionalAnswerType)) {
          finValues.current = {
            ...finValues.current,
            [additionalAnswerType]: {
              taskStepId: id,
              question: additionalAnswerOptions,
              answers,
            },
          };
        }
      }
    );
  }, [list]);

  return (
    <div className={styles.instructions}>
      <div className={styles.instructionsHeader}>
        <div
          className={cx(styles.instructionsItem, styles.instructionsHeaderCell)}
        >
          {t('No.')}
        </div>
        <div
          className={cx(styles.instructionsItem, styles.instructionsHeaderCell)}
        >
          {t('Task')}
        </div>
        <div
          className={cx(styles.instructionsItem, styles.instructionsHeaderCell)}
        >
          {t('Completed')}
        </div>
      </div>
      {isEstateDispositionSummaryDownloading && (
        <div className={styles.instructionsDownloadOverlay}>
          <div className={styles.instructionsDownloadOverlayContent}>
            <CircularProgress color="success" />
            <span>{t('Downloading report')}</span>
          </div>
        </div>
      )}
      <div className={styles.instructionsBody} onClick={onInstructionsClick}>
        {list.map(
          (
            {
              description,
              descriptionFr,
              isDone,
              position,
              id,
              notes,
              additionalAnswerType,
              additionalAnswerOptions,
              answers,
            },
            index
          ) => {
            return (
              <div
                key={id}
                className={cx(
                  styles.instructionsBodyItem,
                  isDone && styles['instructionsBodyItem-done']
                )}
              >
                <div
                  className={cx(
                    styles.instructionsItem,
                    styles.instructionsBodyItemCell
                  )}
                >
                  {position}.
                </div>
                <div
                  className={cx(
                    styles.instructionsItem,
                    styles.instructionsBodyItemCell
                  )}
                >
                  <div>
                    {ReactBB(
                      getLocalizedField(
                        { description, descriptionFr },
                        'description'
                      )
                    )}
                  </div>
                  {additionalAnswerType === 'single' && (
                    <SingleAnswerForm
                      options={additionalAnswerOptions}
                      stepId={id}
                      answers={answers}
                      isLastTask={list.length - 1 === index}
                      updateTask={updateTask}
                    />
                  )}
                  <Notes list={notes} stepId={id} />
                </div>
                <div
                  className={cx(
                    styles.instructionsItem,
                    styles.instructionsBodyItemCell
                  )}
                >
                  <div>
                    <Checkbox
                      checked={isDone}
                      onChange={({ target: { checked } }) =>
                        updateTask(checked, id, list.length - 1 === index)
                      }
                      disabled={
                        loadingTasks ||
                        disabled ||
                        additionalAnswerType === 'single'
                      }
                    />
                  </div>
                </div>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};

export default Instructions;
